import { useCallback } from 'react';
import TargetIcon from '@rsos/base-assets/icons/src/TargetIcon';
import Button from '@rsos/base-components/Button';
import { useZoomAndLocationContext } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import {
  GPX_CAPABILITIES,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_ROUTES,
  gspx,
} from '@rsos/gainsight';
import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import { ESRIMap } from '@rsos/maps/providers/esri/types';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import styled from '@rsos/theme/styled';
import { getMostRecentGeoLocation } from '@rsos/utils/getMostRecentLoc';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';

const StyledButton = styled(Button)`
  position: relative;
  bottom: 75px;
  left: 50%;
  transform: translateX(-50%);
  color: ${({ theme }) => theme.colors.primaryBase};
  text-indent: 4px;
`;

interface GoogleMapProps {
  mapProvider: typeof GOOGLE;
  mapInstance: google.maps.Map;
}

interface ESRIMapProps {
  mapProvider: typeof ESRI;
  mapInstance: ESRIMap;
}

type Props = GoogleMapProps | ESRIMapProps;

export const RecenterMapButton = (options: Props) => {
  const { userPannedAway } = useZoomAndLocationContext();
  const selectedCall = useSelectedCall();
  const mostRecentLocation = getMostRecentGeoLocation(selectedCall);
  const trackButtonClick = () => {
    gspx.trackEvent({
      name: 'recenter_map_button',
      type: GPX_EVENT_TYPES.CLICK,
      feature: GPX_FEATURES.GIS,
      capability: GPX_CAPABILITIES.GIS,
      route: GPX_ROUTES.MAP,
    });
  };

  const onClick = useCallback(() => {
    trackButtonClick();
    if (!mostRecentLocation) {
      // eslint-disable-next-line no-console
      console.error(
        'Failed to re-center map. Could not get most recent location.',
      );
      return;
    }

    panMapTo({
      ...options,
      latitude: mostRecentLocation.latitude,
      longitude: mostRecentLocation.longitude,
      zoom: null,
    });
  }, [mostRecentLocation, options]);

  if (!mostRecentLocation || !userPannedAway) {
    return <></>;
  }

  return (
    <StyledButton size="large" btnType="secondary" onClick={onClick}>
      <TargetIcon />
      Re-center Call
    </StyledButton>
  );
};
